/**
 * Trusscore Estimator - Calculation Constants
 *
 * All constants used in material calculations.
 * These values are derived from Trusscore product specifications.
 */

// ============================================================================
// PANEL DIMENSIONS
// ============================================================================

/**
 * Standard width of W&CB and SlatWall panels in inches.
 * Each panel covers 16 inches of wall width.
 */
export const PANEL_WIDTH_INCHES = 16;

/**
 * Standard width of W&CB and SlatWall panels in feet.
 */
export const PANEL_WIDTH_FEET = PANEL_WIDTH_INCHES / 12; // 1.333... feet

/**
 * Available W&CB panel lengths in feet.
 */
export const PANEL_LENGTHS = [8, 10, 12, 14, 16, 20] as const;
export type PanelLength = (typeof PANEL_LENGTHS)[number];

/**
 * Maximum available panel length.
 */
export const MAX_PANEL_LENGTH = 20;

// ============================================================================
// SLATWALL DIMENSIONS
// ============================================================================

/**
 * Standard length of SlatWall panels in feet.
 * SlatWall only comes in 8' lengths.
 */
export const SLATWALL_PANEL_LENGTH = 8;

/**
 * Height coverage of one SlatWall panel row in feet.
 * Each row is 6 inches (0.5 feet).
 */
export const SLATWALL_COVERAGE_HEIGHT = 0.5;

/**
 * SlatWall row height in inches.
 */
export const SLATWALL_ROW_HEIGHT_INCHES = 6;

/**
 * Transition space between SlatWall and W&CB panels above.
 * 1 inch gap for H-trim installation.
 */
export const SLATWALL_TRANSITION_SPACE_INCHES = 1;
export const SLATWALL_TRANSITION_SPACE_FEET = 1 / 12;

// ============================================================================
// TRIM DIMENSIONS
// ============================================================================

/**
 * Standard length of all trim products in feet.
 * All Trusscore trims come in 10' lengths.
 */
export const TRIM_STOCK_LENGTH = 10;

// ============================================================================
// SCREW CALCULATIONS
// ============================================================================

/**
 * Screws required per linear foot of trim.
 * Formula: 1 screw per 24 inches = 0.5 screws per foot
 */
export const SCREWS_PER_TRIM_FOOT = 0.5;

/**
 * Screws required per square foot of panel coverage.
 * Formula: 1 screw per 2 square feet
 */
export const SCREWS_PER_PANEL_SQFT = 0.5;

/**
 * Screw spacing for trim in inches.
 */
export const TRIM_SCREW_SPACING_INCHES = 24;

/**
 * Screw coverage per screw for panels in square feet.
 */
export const PANEL_SQFT_PER_SCREW = 2;

// ============================================================================
// CALCULATION HELPERS
// ============================================================================

/**
 * Convert inches to feet.
 */
export function inchesToFeet(inches: number): number {
  return inches / 12;
}

/**
 * Convert feet to inches.
 */
export function feetToInches(feet: number): number {
  return feet * 12;
}

/**
 * Calculate number of panel strips needed for a given width.
 * Uses Math.ceil() - always round up to ensure full coverage.
 * @param widthFeet - Width to cover in feet
 * @returns Number of panel strips (each 16" wide)
 */
export function calculatePanelStripsForWidth(widthFeet: number): number {
  const widthInches = feetToInches(widthFeet);
  return Math.ceil(widthInches / PANEL_WIDTH_INCHES);
}

/**
 * Calculate number of SlatWall rows for a given height.
 * @param heightFeet - Height to cover in feet
 * @returns Number of SlatWall rows (each 6" tall)
 */
export function calculateSlatWallRowsForHeight(heightFeet: number): number {
  return Math.ceil(heightFeet / SLATWALL_COVERAGE_HEIGHT);
}

/**
 * Calculate number of trim pieces needed for a given length.
 * @param lengthFeet - Total length to cover in feet
 * @returns Number of trim pieces (each 10' long)
 */
export function calculateTrimPiecesForLength(lengthFeet: number): number {
  return Math.ceil(lengthFeet / TRIM_STOCK_LENGTH);
}


// ============================================================================
// VALIDATION CONSTANTS
// ============================================================================

/**
 * Maximum supported room dimension in feet.
 * Extended from 100ft to 200ft for commercial projects.
 */
export const MAX_ROOM_DIMENSION = 200;

/**
 * Minimum supported room dimension in feet.
 */
export const MIN_ROOM_DIMENSION = 1;

/**
 * Maximum supported wall height in feet.
 */
export const MAX_WALL_HEIGHT = 20;

/**
 * Minimum supported wall height in feet.
 */
export const MIN_WALL_HEIGHT = 1;

/**
 * Tolerance for BOM validation (5%).
 */
export const BOM_VALIDATION_TOLERANCE = 0.05;

// ============================================================================
// BOM SMOOTHING CONSTANTS
// ============================================================================

/**
 * Default orphan threshold - SKUs with quantity <= this are candidates for smoothing.
 * A threshold of 2 means SKUs with 1 or 2 units are considered orphans.
 */
export const DEFAULT_ORPHAN_THRESHOLD = 2;

/**
 * Default maximum waste increase percentage (25%).
 * Substitutions that would increase waste by more than this are rejected.
 */
export const DEFAULT_MAX_WASTE_INCREASE_PERCENT = 0.25;

/**
 * Preset smoothing configurations for different use cases.
 */
export const SMOOTHING_PRESETS = {
  /** Minimal smoothing - only eliminate single-unit orphans with low waste impact */
  conservative: {
    enabled: true,
    orphanThreshold: 1,
    maxWasteIncreasePercent: 0.10,
  },
  /** Balanced smoothing - good tradeoff between SKU reduction and waste (default) */
  balanced: {
    enabled: true,
    orphanThreshold: 2,
    maxWasteIncreasePercent: 0.20,
  },
  /** Aggressive smoothing - maximize SKU reduction, accept more waste */
  aggressive: {
    enabled: true,
    orphanThreshold: 3,
    maxWasteIncreasePercent: 0.30,
  },
} as const;

/**
 * Default smoothing configuration (balanced preset, disabled by default).
 * Users must opt-in to smoothing.
 */
export const DEFAULT_SMOOTHING_CONFIG = {
  enabled: false,
  orphanThreshold: DEFAULT_ORPHAN_THRESHOLD,
  maxWasteIncreasePercent: DEFAULT_MAX_WASTE_INCREASE_PERCENT,
};

export default {
  PANEL_WIDTH_INCHES,
  PANEL_WIDTH_FEET,
  PANEL_LENGTHS,
  MAX_PANEL_LENGTH,
  SLATWALL_PANEL_LENGTH,
  SLATWALL_COVERAGE_HEIGHT,
  SLATWALL_TRANSITION_SPACE_FEET,
  TRIM_STOCK_LENGTH,
  SCREWS_PER_TRIM_FOOT,
  SCREWS_PER_PANEL_SQFT,
};
